"use client"

import {
  CONTACTOUT_SENIORITY_LEVEL_OPTIONS,
  CONTACTOUT_YEARS_IN_CURRENT_ROLE_OPTIONS,
} from "@/types/contactOut"

export interface FilterLocationFullItem {
  city?: string | null
  region?: string | null
  country?: "US" | "CA" | null
  label?: string
}

export interface FilterFormData {
  minExperience: string
  maxExperience: string
  requiredContactInfo: string
  hideViewedProfiles: string
  onlyConnections: string
  locations: string[]
  locationInput: string
  timezone: string
  pastLocations: string
  locationCountry: string
  locationRegion: string[]
  locationFull: FilterLocationFullItem[]
  jobTitles: string[]
  jobTitleInput: string
  currentOnly: string
  timeSpentMin: string[]
  timeSpentMax: string
  minimumTenure: string
  pastJobTitles: string
  jobTitleLevels: string[]
  jobTitleRoles: string
  companies: string[]
  companyInput: string
  companyType: string
  excludedCompanies: string[]
  excludedCompanyInput: string
  excludedCompanyType: string
  removeDNCCompanies: boolean
  companyIndustries: string[]
  companyTags: string
  companyHQLocations: string
  companySizes: string[]
  companyFundedAfter: string
  companyFundingStages: string
  estimatedRevenue: string
  minFundingRaised: string
  skills: string[]
  skillInput: string
  keywords: string[]
  keywordsInput: string
  hideInactiveFilters: boolean
}

export interface SimpleFilterData {
  minExperience?: string
  maxExperience?: string
  locationCity?: string[]
  locationRegion?: string[]
  jobTitles: string[]
  companies?: string[]
  companyIndustries?: string[]
  companyTags?: string
  skills?: string[]
  locationCountry?: string | string[]
  location_full?: FilterLocationFullItem[]
  tenure?: string[]
  seniority?: string[]
  org_sizes?: string[]
  keywords?: string[]
  current_titles_only?: boolean
}

export interface CleanFilterData {
  minExperience?: string
  maxExperience?: string
  requiredContactInfo?: string
  hideViewedProfiles?: string
  onlyConnections?: string
  locations?: string[]
  timezone?: string
  pastLocations?: string
  locationCountry?: "US" | "CA"
  locationFull?: FilterLocationFullItem[]
  jobTitles?: string[]
  currentOnly?: string
  timeSpentMin?: string[]
  timeSpentMax?: string
  minimumTenure?: string
  pastJobTitles?: string
  jobTitleLevels?: string[]
  jobTitleRoles?: string
  companies?: string[]
  companyType?: string
  excludedCompanies?: string[]
  excludedCompanyType?: string
  removeDNCCompanies?: boolean
  companyIndustries?: string[]
  companyTags?: string
  companyHQLocations?: string
  companySizes?: string[]
  companyFundedAfter?: string
  companyFundingStages?: string
  estimatedRevenue?: string
  minFundingRaised?: string
  skills?: string[]
}

export type SearchFiltersDraft = Omit<
  FilterFormData,
  | "locationInput"
  | "jobTitleInput"
  | "companyInput"
  | "excludedCompanyInput"
  | "skillInput"
  | "keywordsInput"
  | "hideInactiveFilters"
>

export interface FilterSummaryItem {
  id: string
  label: string
  value: string
}

export const DEFAULT_FILTER_VALUES: FilterFormData = {
  minExperience: "",
  maxExperience: "",
  requiredContactInfo: "match-any",
  hideViewedProfiles: "dont-hide",
  onlyConnections: "restrict",
  locations: [],
  locationInput: "",
  timezone: "",
  pastLocations: "",
  locationCountry: "",
  locationRegion: [],
  locationFull: [],
  jobTitles: [],
  jobTitleInput: "",
  currentOnly: "current-only",
  timeSpentMin: [],
  timeSpentMax: "no-maximum",
  minimumTenure: "",
  pastJobTitles: "",
  jobTitleLevels: [],
  jobTitleRoles: "",
  companies: [],
  companyInput: "",
  companyType: "current-past",
  excludedCompanies: [],
  excludedCompanyInput: "",
  excludedCompanyType: "current-only",
  removeDNCCompanies: false,
  companyIndustries: [],
  companyTags: "",
  companyHQLocations: "",
  companySizes: [],
  companyFundedAfter: "",
  companyFundingStages: "series-b",
  estimatedRevenue: "",
  minFundingRaised: "",
  skills: [],
  skillInput: "",
  keywords: [],
  keywordsInput: "",
  hideInactiveFilters: false,
}

export const DEFAULT_SEARCH_FILTERS_DRAFT: SearchFiltersDraft = {
  minExperience: "",
  maxExperience: "",
  requiredContactInfo: "match-any",
  hideViewedProfiles: "dont-hide",
  onlyConnections: "restrict",
  locations: [],
  timezone: "",
  pastLocations: "",
  locationCountry: "",
  locationRegion: [],
  locationFull: [],
  jobTitles: [],
  currentOnly: "current-only",
  timeSpentMin: [],
  timeSpentMax: "no-maximum",
  minimumTenure: "",
  pastJobTitles: "",
  jobTitleLevels: [],
  jobTitleRoles: "",
  companies: [],
  companyType: "current-past",
  excludedCompanies: [],
  excludedCompanyType: "current-only",
  removeDNCCompanies: false,
  companyIndustries: [],
  companyTags: "",
  companyHQLocations: "",
  companySizes: [],
  companyFundedAfter: "",
  companyFundingStages: "series-b",
  estimatedRevenue: "",
  minFundingRaised: "",
  skills: [],
  keywords: [],
}

export function normalizeStructuredLocation(location: FilterLocationFullItem): FilterLocationFullItem {
  const city = location.city?.trim() || null
  const region = location.region?.trim() || null
  const country = location.country ?? null
  const label = location.label?.trim() || [city, region].filter(Boolean).join(", ") || city || region || ""

  return {
    city,
    region,
    country,
    label,
  }
}

const toUniqueList = (values: Array<string | null | undefined> = []) =>
  Array.from(new Set(values.map((value) => value?.trim()).filter(Boolean) as string[]))

const findLabel = (value: string, options: Array<{ value: string; label: string }>) =>
  options.find((option) => option.value === value)?.label ?? value

export function buildFilterFormDataFromDraft(draft: Partial<SearchFiltersDraft> = {}): FilterFormData {
  const formValues: FilterFormData = {
    ...DEFAULT_FILTER_VALUES,
    ...draft,
    locations: [...(draft.locations ?? [])],
    locationRegion: [...(draft.locationRegion ?? [])],
    locationFull: [...(draft.locationFull ?? [])],
    jobTitles: [...(draft.jobTitles ?? [])],
    timeSpentMin: [...(draft.timeSpentMin ?? [])],
    jobTitleLevels: [...(draft.jobTitleLevels ?? [])],
    companies: [...(draft.companies ?? [])],
    excludedCompanies: [...(draft.excludedCompanies ?? [])],
    companyIndustries: [...(draft.companyIndustries ?? [])],
    companySizes: [...(draft.companySizes ?? [])],
    skills: [...(draft.skills ?? [])],
    keywords: [...(draft.keywords ?? [])],
  }

  const normalizedStructuredLocations = formValues.locationFull
    .map((location) => normalizeStructuredLocation(location))
    .filter((location) => location.city || location.region || location.country || location.label)

  formValues.locationFull = normalizedStructuredLocations

  normalizedStructuredLocations.forEach((location) => {
    if (location.city && !formValues.locations.includes(location.city)) {
      formValues.locations.push(location.city)
    }

    if (location.region && !formValues.locationRegion.includes(location.region)) {
      formValues.locationRegion.push(location.region)
    }

    if (!formValues.locationCountry && location.country) {
      formValues.locationCountry = location.country
    }
  })

  return formValues
}

export function extractSearchFiltersDraft(formData: FilterFormData): SearchFiltersDraft {
  return {
    minExperience: formData.minExperience,
    maxExperience: formData.maxExperience,
    requiredContactInfo: formData.requiredContactInfo,
    hideViewedProfiles: formData.hideViewedProfiles,
    onlyConnections: formData.onlyConnections,
    locations: [...formData.locations],
    timezone: formData.timezone,
    pastLocations: formData.pastLocations,
    locationCountry: formData.locationCountry,
    locationRegion: [...formData.locationRegion],
    locationFull: formData.locationFull.map((location) => normalizeStructuredLocation(location)),
    jobTitles: [...formData.jobTitles],
    currentOnly: formData.currentOnly,
    timeSpentMin: [...formData.timeSpentMin],
    timeSpentMax: formData.timeSpentMax,
    minimumTenure: formData.minimumTenure,
    pastJobTitles: formData.pastJobTitles,
    jobTitleLevels: [...formData.jobTitleLevels],
    jobTitleRoles: formData.jobTitleRoles,
    companies: [...formData.companies],
    companyType: formData.companyType,
    excludedCompanies: [...formData.excludedCompanies],
    excludedCompanyType: formData.excludedCompanyType,
    removeDNCCompanies: formData.removeDNCCompanies,
    companyIndustries: [...formData.companyIndustries],
    companyTags: formData.companyTags,
    companyHQLocations: formData.companyHQLocations,
    companySizes: [...formData.companySizes],
    companyFundedAfter: formData.companyFundedAfter,
    companyFundingStages: formData.companyFundingStages,
    estimatedRevenue: formData.estimatedRevenue,
    minFundingRaised: formData.minFundingRaised,
    skills: [...formData.skills],
    keywords: [...formData.keywords],
  }
}

export function simpleFiltersToDraft(simpleFilters?: Partial<SimpleFilterData>): SearchFiltersDraft {
  const locationCountry = Array.isArray(simpleFilters?.locationCountry)
    ? simpleFilters?.locationCountry?.[0] ?? ""
    : simpleFilters?.locationCountry ?? ""

  return buildFilterFormDataFromDraft({
    ...DEFAULT_SEARCH_FILTERS_DRAFT,
    minExperience: simpleFilters?.minExperience ?? "",
    maxExperience: simpleFilters?.maxExperience ?? "",
    locations: toUniqueList(simpleFilters?.locationCity),
    locationRegion: toUniqueList(simpleFilters?.locationRegion),
    locationCountry,
    locationFull: simpleFilters?.location_full ?? [],
    jobTitles: toUniqueList(simpleFilters?.jobTitles ?? []),
    companies: toUniqueList(simpleFilters?.companies ?? []),
    companyIndustries: toUniqueList(simpleFilters?.companyIndustries ?? []),
    companyTags: simpleFilters?.companyTags ?? "",
    skills: toUniqueList(simpleFilters?.skills ?? []),
    timeSpentMin: toUniqueList(simpleFilters?.tenure ?? []),
    jobTitleLevels: toUniqueList(simpleFilters?.seniority ?? []),
    companySizes: toUniqueList(simpleFilters?.org_sizes ?? []),
    keywords: toUniqueList(simpleFilters?.keywords ?? []),
    currentOnly: simpleFilters?.current_titles_only === false ? "current-past" : "current-only",
  })
}

export function buildBooleanPreview(filters: SearchFiltersDraft): string {
  const parts: string[] = []

  if (filters.locations.length > 0) {
    parts.push(`location:${filters.locations.join(", ")}`)
  }

  if (filters.jobTitles.length > 0) {
    parts.push(filters.jobTitles.map((title) => `title:\"${title}\"`).join(" OR "))
  }

  if (filters.minExperience || filters.maxExperience) {
    const range = [filters.minExperience, filters.maxExperience].filter(Boolean).join("-")
    if (range) {
      parts.push(`experience:${range}+`)
    }
  }

  if (filters.skills.length > 0) {
    parts.push(filters.skills.map((skill) => `skill:${skill}`).join(" AND "))
  }

  if (filters.companyIndustries.length > 0) {
    parts.push(filters.companyIndustries.map((industry) => `industry:${industry}`).join(" OR "))
  }

  if (filters.keywords.length > 0) {
    parts.push(filters.keywords.map((keyword) => `\"${keyword}\"`).join(" AND "))
  }

  if (filters.companies.length > 0) {
    parts.push(filters.companies.map((company) => `company:\"${company}\"`).join(" OR "))
  }

  return parts.join(" AND ")
}

export function buildFilterSummary(filters: SearchFiltersDraft): FilterSummaryItem[] {
  const summary: FilterSummaryItem[] = []

  const addSummary = (id: string, label: string, value?: string) => {
    if (!value) return
    summary.push({ id, label, value })
  }

  const structuredLocationSummaries = filters.locationFull
    .map((location, index) => {
      const normalizedLocation = normalizeStructuredLocation(location)

      if (normalizedLocation.city && normalizedLocation.region) {
        return {
          id: `structured-location-${index}`,
          label: "City",
          value: `${normalizedLocation.city}, ${normalizedLocation.region}`,
        }
      }

      if (normalizedLocation.city) {
        return {
          id: `structured-location-${index}`,
          label: "City",
          value: normalizedLocation.city,
        }
      }

      if (normalizedLocation.region) {
        return {
          id: `structured-location-${index}`,
          label: "Region",
          value: normalizedLocation.region,
        }
      }

      return null
    })
    .filter((item): item is FilterSummaryItem => Boolean(item))

  const uniqueStructuredLocationSummaries = structuredLocationSummaries.filter(
    (item, index, items) => items.findIndex((candidate) => candidate.label === item.label && candidate.value === item.value) === index,
  )

  if (uniqueStructuredLocationSummaries.length > 0) {
    summary.push(...uniqueStructuredLocationSummaries)
  } else {
    addSummary("locations", "Location", filters.locations.join(", "))
    addSummary("regions", "Region", filters.locationRegion.join(", "))
    addSummary("country", "Country", filters.locationCountry)
  }

  addSummary("titles", "Job Title", filters.jobTitles.join(", "))

  if (filters.minExperience || filters.maxExperience) {
    addSummary(
      "experience",
      "Experience",
      filters.minExperience && filters.maxExperience
        ? `${filters.minExperience}-${filters.maxExperience} years`
        : filters.minExperience
          ? `${filters.minExperience}+ years`
          : `0-${filters.maxExperience} years`,
    )
  }

  addSummary("skills", "Skills", filters.skills.join(", "))
  addSummary("industries", "Industry", filters.companyIndustries.join(", "))
  addSummary("companies", "Companies", filters.companies.join(", "))
  addSummary("excluded-companies", "Excluded", filters.excludedCompanies.join(", "))
  addSummary("keywords", "Keywords", filters.keywords.join(", "))

  addSummary(
    "tenure",
    "Tenure",
    filters.timeSpentMin.map((value) =>
      findLabel(value, CONTACTOUT_YEARS_IN_CURRENT_ROLE_OPTIONS as Array<{ value: string; label: string }>),
    ).join(", "),
  )

  addSummary(
    "seniority",
    "Seniority",
    filters.jobTitleLevels.map((value) =>
      findLabel(value, CONTACTOUT_SENIORITY_LEVEL_OPTIONS as Array<{ value: string; label: string }>),
    ).join(", "),
  )

  addSummary("company-sizes", "Company Size", filters.companySizes.join(", "))

  if (filters.currentOnly !== "current-only") {
    addSummary("current-only", "Titles", "Current + Past")
  }

  return summary
}

export const cleanFilterData = (filterData: FilterFormData): CleanFilterData => {
  const cleaned: CleanFilterData = {}

  if (filterData.minExperience) cleaned.minExperience = filterData.minExperience
  if (filterData.maxExperience) cleaned.maxExperience = filterData.maxExperience
  if (filterData.requiredContactInfo && filterData.requiredContactInfo !== "match-any") {
    cleaned.requiredContactInfo = filterData.requiredContactInfo
  }
  if (filterData.hideViewedProfiles && filterData.hideViewedProfiles !== "dont-hide") {
    cleaned.hideViewedProfiles = filterData.hideViewedProfiles
  }
  if (filterData.onlyConnections && filterData.onlyConnections !== "restrict") {
    cleaned.onlyConnections = filterData.onlyConnections
  }

  if (filterData.locations.length > 0) cleaned.locations = filterData.locations
  if (filterData.timezone) cleaned.timezone = filterData.timezone
  if (filterData.pastLocations) cleaned.pastLocations = filterData.pastLocations
  if (filterData.locationFull.length > 0) cleaned.locationFull = filterData.locationFull

  if (filterData.jobTitles.length > 0) cleaned.jobTitles = filterData.jobTitles
  if (filterData.currentOnly && filterData.currentOnly !== "current-only") {
    cleaned.currentOnly = filterData.currentOnly
  }
  if (filterData.timeSpentMin.length) {
    cleaned.timeSpentMin = filterData.timeSpentMin
  }
  if (filterData.timeSpentMax && filterData.timeSpentMax !== "no-maximum") {
    cleaned.timeSpentMax = filterData.timeSpentMax
  }
  if (filterData.minimumTenure) cleaned.minimumTenure = filterData.minimumTenure
  if (filterData.pastJobTitles) cleaned.pastJobTitles = filterData.pastJobTitles
  if (filterData.jobTitleLevels.length > 0) cleaned.jobTitleLevels = filterData.jobTitleLevels
  if (filterData.jobTitleRoles) cleaned.jobTitleRoles = filterData.jobTitleRoles

  if (filterData.companies.length > 0) cleaned.companies = filterData.companies
  if (filterData.companyType && filterData.companyType !== "current-past") {
    cleaned.companyType = filterData.companyType
  }
  if (filterData.excludedCompanies.length > 0) cleaned.excludedCompanies = filterData.excludedCompanies
  if (filterData.excludedCompanyType && filterData.excludedCompanyType !== "current-only") {
    cleaned.excludedCompanyType = filterData.excludedCompanyType
  }
  if (filterData.removeDNCCompanies) cleaned.removeDNCCompanies = filterData.removeDNCCompanies
  if (filterData.companyIndustries.length > 0) cleaned.companyIndustries = filterData.companyIndustries
  if (filterData.companyTags) cleaned.companyTags = filterData.companyTags
  if (filterData.companyHQLocations) cleaned.companyHQLocations = filterData.companyHQLocations
  if (filterData.companySizes) cleaned.companySizes = filterData.companySizes
  if (filterData.companyFundedAfter) cleaned.companyFundedAfter = filterData.companyFundedAfter
  if (filterData.companyFundingStages && filterData.companyFundingStages !== "series-b") {
    cleaned.companyFundingStages = filterData.companyFundingStages
  }
  if (filterData.estimatedRevenue) cleaned.estimatedRevenue = filterData.estimatedRevenue
  if (filterData.minFundingRaised) cleaned.minFundingRaised = filterData.minFundingRaised

  if (filterData.skills.length > 0) cleaned.skills = filterData.skills

  if (filterData.locationCountry) cleaned.locationCountry = filterData.locationCountry as "US" | "CA"

  return cleaned
}
